/* eslint-disable arrow-body-style */
import {
  getFormattedTime,
  getPriceText,
  capitalizeFirstLetter,
  addCommas,
} from '@zorba-shared/core/Services/textService';
import {
  DEAL_SUBMISSION_MATCH_STATUS_TITLES,
  getLatestAskingPriceBySeller,
  DEFAULT_FEE_AMOUNT,
} from '@zorba-shared/core/Constants/offer';
import { Tag } from 'antd';

import { Link } from 'react-router-dom';
import { IoMdCheckmark } from '@react-icons/all-files/io/IoMdCheckmark';
import { IoMdClose } from '@react-icons/all-files/io/IoMdClose';
import { PROPERTY_TYPE_TITLES } from '@zorba-shared/core/Constants/deal';
import { ZorbaButton } from '../../../../components/forms';
import { BUTTON_VARIANT_LINK } from '../../../../components/forms/ZorbaButton/constants';
import { setSelectedOffer } from '../../../../reducers/Offer/actions';

import './index.scss';
import { reportEvent } from '../../../../services/analyticsService';
import { isMobile } from '../../../../services/clientService';

const returnPropertyValueOrPlaceholder = (value) =>
  value != null && value !== '' ? value : 'N/A';

export const getColumns = (dispatch, doesIncludeFeeInOffer) => [
  {
    title: 'About the deal',
    children: [
      {
        title: 'Offer Request Date',
        key: 'offerDate',
        dataIndex: 'createdAt',
        sorter: (a, b) => new Date(a?.createdAt) - new Date(b?.createdAt),
        render: (createdAt) => getFormattedTime(createdAt, 'MM/DD/YYYY'),
        width: 200,
      },
      {
        title: 'Last updated at',
        key: 'lastUpdatedAt',
        dataIndex: 'updatedAt',
        sorter: (a, b) => new Date(a?.createdAt) - new Date(b?.createdAt),
        render: (updatedAt) => getFormattedTime(updatedAt, 'MM/DD/YYYY'),
        width: 200,
      },
      {
        title: 'Source',
        key: 'source',
        render: ({ dealSubmission }) => (
          <Tag>
            {dealSubmission?.kind === 'pre-deal' ? 'Pre-Lock' : 'Wholesale'}
          </Tag>
        ),
      },
      {
        title: '',
        key: 'isFlip',
        render: ({ isFlip }) => (isFlip ? <Tag>Flip opportunity</Tag> : ''),
      },
    ],
  },
  {
    title: 'About the property',
    children: [
      {
        title: 'Property Address',
        key: 'address',
        render: (match) =>
          returnPropertyValueOrPlaceholder(
            match?.dealSubmission?.address?.formattedAddress,
          ),
        width: 200,
      },
      {
        title: 'Market',
        key: 'market',
        width: 100,
        render: (data) => <Tag>{data.matchedBuyBox?.title}</Tag>,
      },
      {
        title: 'County',
        key: 'county',
        width: 100,
        render: (data) =>
          returnPropertyValueOrPlaceholder(
            data?.dealSubmission?.address?.county,
          ),
      },
      {
        title: 'Property type',
        key: 'propertyType',
        render: (data) =>
          returnPropertyValueOrPlaceholder(
            PROPERTY_TYPE_TITLES[
              data?.dealSubmission?.lastCalculationParams?.propertyType
            ],
          ),
      },
      {
        title: 'SqFt',
        key: 'sqft',
        render: ({ dealSubmission }) =>
          returnPropertyValueOrPlaceholder(
            addCommas(dealSubmission?.lastCalculationParams?.squareFootage),
          ),
      },
      {
        title: 'Lot size',
        key: 'lotSize',
        render: ({ dealSubmission }) =>
          returnPropertyValueOrPlaceholder(
            addCommas(dealSubmission?.lastCalculationParams?.lotSize),
          ),
      },
      {
        title: 'Year built',
        key: 'yearBuilt',
        render: ({ dealSubmission }) =>
          returnPropertyValueOrPlaceholder(
            dealSubmission?.lastCalculationParams?.yearBuilt,
          ),
      },
      {
        title: 'Beds',
        key: 'bedrooms',
        render: ({ dealSubmission } = {}) =>
          returnPropertyValueOrPlaceholder(
            dealSubmission?.lastCalculationParams?.bedroom,
          ),
      },
      {
        title: 'Baths',
        key: 'bathrooms',
        render: ({ dealSubmission } = {}) =>
          returnPropertyValueOrPlaceholder(
            dealSubmission?.lastCalculationParams?.bathroom,
          ),
      },
      {
        title: 'Pool',
        key: 'pool',
        align: 'center',
        render: ({ dealSubmission }) => {
          const hasPool = dealSubmission?.lastCalculationParams?.hasPool;
          if (hasPool == null) {
            return 'N/A';
          }
          return hasPool ? <IoMdCheckmark /> : <IoMdClose />;
        },
      },
      {
        title: 'Parking',
        key: 'parking',
        render: ({ dealSubmission }) =>
          returnPropertyValueOrPlaceholder(
            dealSubmission?.lastCalculationParams?.parkingSpaces,
          ),
      },
      {
        title: 'Occupancy',
        key: 'occupancy',
        render: ({ dealSubmission }) =>
          dealSubmission.userData.occupancy
            ? capitalizeFirstLetter(dealSubmission.userData.occupancy)
            : '',
      },
      {
        title: 'HOA Fee',
        key: 'hoa',
        render: ({ dealSubmission }) =>
          returnPropertyValueOrPlaceholder(
            getPriceText(dealSubmission?.userData?.hoaFee),
          ),
      },
      {
        title: 'HOA Period',
        key: 'hoaPeriod',
        render: ({ dealSubmission }) =>
          returnPropertyValueOrPlaceholder(
            capitalizeFirstLetter(dealSubmission?.userData?.hoaPeriod),
          ),
      },
      {
        title: 'Estimated rent',
        key: 'rent',
        render: ({ dealSubmission }) =>
          dealSubmission?.userData?.rent ||
          dealSubmission?.lastCalculationParams?.grossRent
            ? getPriceText(
                parseInt(
                  dealSubmission?.userData?.rent ||
                    dealSubmission?.lastCalculationParams?.grossRent,
                  10,
                ),
              )
            : 'N/A',
      },
      {
        title: 'Avg School Rating',
        key: 'rating',
        render: ({ dealSubmission }) => {
          const { schoolRating } = dealSubmission?.zillowData || {};
          return schoolRating ? schoolRating.toFixed(2) : 'N/A';
        },
      },
    ],
  },
  {
    title: 'Seller',
    children: [
      {
        title: 'Asking price (Seller)',
        key: 'askingPriceSeller',
        render: ({ dealSubmission, feeAmount }) => {
          const lastAskingPrice = getLatestAskingPriceBySeller(dealSubmission);

          return doesIncludeFeeInOffer
            ? getPriceText(lastAskingPrice + (feeAmount || DEFAULT_FEE_AMOUNT))
            : getPriceText(lastAskingPrice);
        },
      },
      {
        title: 'Photos/Videos',
        key: 'linkToPhotos',
        render: ({ dealSubmission }) => (
          <Link target="_blank" to={`/deal/photos/${dealSubmission?._id}`}>
            {' '}
            View photos
          </Link>
        ),
      },
    ],
  },
  {
    title: '',
    children: [
      {
        title: 'Your offer',
        key: 'buyerOffer',
        render: (_, { lastOffer }) => {
          if (lastOffer) {
            return doesIncludeFeeInOffer
              ? getPriceText(parseInt(lastOffer.rawAmount, 10))
              : getPriceText(parseInt(lastOffer.amount, 10));
          }
          return '';
        },
        editable: true,
        dataIndex: 'buyerOffer',
        fixed: isMobile() ? '' : 'right',
      },
      {
        title: 'Status',
        key: 'buyerOffer',
        render: (data) => {
          return <Tag>{DEAL_SUBMISSION_MATCH_STATUS_TITLES[data?.status]}</Tag>;
        },
        fixed: isMobile() ? '' : 'right',
      },
      {
        fixed: 'right',
        key: 'actions',
        render: (_, record) => {
          return (
            <ZorbaButton
              onClick={() => {
                reportEvent('dashboard', 'view_deal_click');
                dispatch(setSelectedOffer(record));
              }}
              variant={BUTTON_VARIANT_LINK}
            >
              View
            </ZorbaButton>
          );
        },
      },
    ],
  },
];
